import { last, shuffle } from 'lodash';
import { RANDOMIZE_POLL_ALL, RANDOMIZE_POLL_GROUPS, RANDOMIZE_POLL_SETS } from 'schema/constants';
import type { Poll, Question } from 'schema/poll';
import { ICState } from 'schema/state';
import { getQuestionGroupId, selectGroup, selectQuestionDeck, selectSet } from '../selects';
import { shuffleWithExcluded } from '../utils';

export function pollPreStartTransform({
  poll,
  links,
  questionsIndices,
}: {
  poll: Poll;
  links: ICState['links'];
  questionsIndices: ICState['questionsIndices'];
}): { poll: Poll; questionsIndices: string[] | undefined; links: ICState['links'] } {
  let result = poll;

  if (questionsIndices?.length && !poll.useServerPass) {
    const questions: Question[] = [];
    questionsIndices.forEach(questionId => {
      const found = poll.questions.find(it => it.id === questionId);
      if (found) questions.push(found);
    });

    const firstId = getFirstId(poll, questions);
    result = { ...result, firstId, questions };
  } else {
    result = randomizePoll(result);

    result = randomizeSetScreens(result);

    result = randomizeGroupQuestions(result);

    result = randomizeDeckQuestions(result);

    result = removeAnswersWithoutId(result);

    questionsIndices = result.questions.map(question => question.id);
  }

  if (!links) {
    const { poll: nextPoll, links: nextLinks } = resolveLinkedQuestions(result);
    result = nextPoll;
    links = nextLinks;
  }

  return { poll: result, questionsIndices, links };
}

type IGroupedQuestion =
  | {
      id: string;
      text?: string;
      setId?: string;
      items: Question[];
      type: 'question' | 'auth';
    }
  | {
      id: string;
      text?: string;
      setId?: string;
      items: Question[];
      randomizeGroupExcludedIds?: string[];
      type: 'group' | 'matrix' | 'card_sorting';
    };

export function groupQuestionsByType(poll: Poll) {
  const result: IGroupedQuestion[] = [];
  poll.questions.forEach(question => {
    const lastItem = last(result);
    const groupId = getQuestionGroupId(question, poll.questionDecks);
    if (groupId && lastItem && lastItem?.id === groupId) {
      lastItem.items.push(question);
    } else if (groupId) {
      const group = selectGroup(poll, groupId);
      result.push({
        type: 'group',
        items: [question],
        setId: group.setId,
        id: groupId,
        text: group.data.name || group.data.text,
        randomizeGroupExcludedIds: group.data.randomizeGroupExcludedIds,
      });
    } else if (question.data.type === 'POLL_AUTH') {
      result.push({
        type: 'auth',
        id: question.id,
        items: [question],
        setId: question.setId,
        text: question.data.text,
      });
    } else {
      result.push({
        id: question.id,
        type: 'question',
        items: [question],
        setId: question.setId,
        text: question.data.text,
      });
    }
  });
  return result;
}

type IGroupedScreen = IGroupedQuestion | { type: 'set'; id: string; items: IGroupedQuestion[] };

/** Группирует сеты, где 1 set = 1 screen */
function groupScreensByType(groupedQuestions: IGroupedQuestion[]): IGroupedScreen[] {
  const result: IGroupedScreen[] = [];
  const groupedSets: { [setId: string]: IGroupedQuestion[] } = {};

  groupedQuestions.forEach(grouped => {
    if (grouped.setId) {
      if (!groupedSets[grouped.setId]) {
        groupedSets[grouped.setId] = [];
        result.push({ type: 'set', id: grouped.setId, items: groupedSets[grouped.setId]! });
      }
      groupedSets[grouped.setId]?.push(grouped);
    } else {
      result.push(grouped);
    }
  });

  return result;
}

/** Разгруппирует сеты, где 1 set = 1 screen */
function ungroupScreensByType(groupedScreens: IGroupedScreen[]): IGroupedQuestion[] {
  const result: IGroupedQuestion[] = [];

  groupedScreens.forEach(grouped => {
    if (grouped.type === 'set') {
      result.push(...grouped.items);
    } else {
      result.push(grouped);
    }
  });

  return result;
}

/** Рандомизирует страницы в сетах, если randomizeSet = true */
function randomizeSetScreens(poll: Poll) {
  let result = poll;

  if (result.sets?.length) {
    const groupedQuestions = groupQuestionsByType(result);
    const groupedScreens = groupScreensByType(groupedQuestions);

    if (groupedScreens.length) {
      const questions = groupedScreens
        .reduce<IGroupedQuestion[]>((result, screen) => {
          if (screen.type === 'set') {
            const set = selectSet(poll, screen.id);
            if (set.data.randomizeSet) {
              return [...result, ...shuffleWithExcluded(screen.items, set.data.randomizeSetExcludedIds)];
            }
            return [...result, ...screen.items];
          }
          return [...result, screen];
        }, [])
        .reduce<Question[]>((result, item) => [...result, ...item.items], []);

      const firstId = getFirstId(poll, questions);
      result = { ...result, firstId, questions };
    }
  }
  return result;
}

export function groupQuestionsByGroupAndDeck(poll: Poll) {
  const result: (
    | { id: string; type: 'question'; items: [Question] }
    | {
        id: string;
        type: 'group';
        items: (
          | { id: string; type: 'question'; items: [Question] }
          | { id: string; type: 'deck'; groupId: string; items: Question[] }
        )[];
      }
  )[] = [];

  poll.questions.forEach(question => {
    const lastItem = last(result);
    const lastGroupLastItem = lastItem?.type === 'group' ? last(lastItem.items) : null;
    const groupId = getQuestionGroupId(question, poll.questionDecks);

    const lastItemInCurrentDeck =
      !!lastGroupLastItem && lastGroupLastItem.type === 'deck' && lastGroupLastItem.id === question.deckId;
    const lastItemInCurrentGroup = lastItem?.type === 'group' && lastItem.id === groupId;

    if (lastItemInCurrentDeck) {
      lastGroupLastItem.items.push(question);
    } else if (lastItemInCurrentGroup) {
      lastItem.items.push({ id: question.id, type: 'question', items: [question] });
    } else if (groupId) {
      result.push({
        type: 'group',
        id: groupId,
        items: [{ id: question.id, type: 'question', items: [question] }],
      });
    } else {
      result.push({ type: 'question', id: question.id, items: [question] });
    }
  });

  return result;
}

export function groupQuestionsByDeck(questions: Question[]) {
  const result: ({ type: 'question'; items: [Question] } | { type: 'deck'; id: string; items: Question[] })[] = [];

  questions.forEach(question => {
    const lastItem = last(result);

    const lastItemInCurrentDeck = lastItem?.type === 'deck' && lastItem.id === question.deckId;

    if (lastItemInCurrentDeck) {
      lastItem.items.push(question);
    } else if (question.deckId) {
      result.push({ type: 'deck', id: question.deckId, items: [question] });
    } else {
      result.push({ type: 'question', items: [question] });
    }
  });

  return result;
}

function randomizeGroupQuestions(poll: Poll) {
  const groupedQuestions = groupQuestionsByGroupAndDeck(poll);

  const randomizedGroupQuestions = groupedQuestions.map(groupOrQuestion => {
    if (groupOrQuestion.type === 'group') {
      const group = selectGroup(poll, groupOrQuestion.id);

      if (group.data.randomizeGroup) {
        return {
          ...groupOrQuestion,
          items: shuffleWithExcluded(groupOrQuestion.items, group.data.randomizeGroupExcludedIds),
        };
      }

      return groupOrQuestion;
    }

    return groupOrQuestion;
  });

  const questions: Question[] = randomizedGroupQuestions.flatMap(groupOrQuestion => {
    if (groupOrQuestion.type === 'group') {
      return groupOrQuestion.items.flatMap(deckOrQuestion => deckOrQuestion.items);
    }

    return groupOrQuestion.items;
  });
  const firstId = getFirstId(poll, questions);

  return { ...poll, firstId, questions };
}

function randomizeDeckQuestions(poll: Poll) {
  const groupedQuestions = groupQuestionsByDeck(poll.questions);

  const randomizedDecks = groupedQuestions.map(deckOrQuestion => {
    if (deckOrQuestion.type === 'deck') {
      const deck = selectQuestionDeck(poll, deckOrQuestion.id);

      if (deck.data.randomizeDeck) {
        const { randomizeableQuestions, stableQuestions } = deckOrQuestion.items.reduce<{
          randomizeableQuestions: Question[];
          stableQuestions: Question[];
        }>(
          (result, question) => {
            if ('exclusiveQuestion' in question.data && question.data.exclusiveQuestion) {
              result.stableQuestions.push(question);
            } else {
              result.randomizeableQuestions.push(question);
            }

            return result;
          },
          { randomizeableQuestions: [], stableQuestions: [] },
        );

        return {
          ...deckOrQuestion,
          items: [
            ...shuffleWithExcluded(randomizeableQuestions, deck.data.randomizeDeckExcludedIds),
            ...stableQuestions,
          ],
        };
      }

      return deckOrQuestion;
    }

    return deckOrQuestion;
  });

  const questions: Question[] = randomizedDecks.flatMap(deckOrQuestion => deckOrQuestion.items);
  const firstId = getFirstId(poll, questions);

  return { ...poll, firstId, questions };
}

function removeAnswersWithoutId(poll: Poll) {
  const hasAnswerWithoutId = !!poll.questions.find(
    question => 'answers' in question.data && question.data.answers.find(answer => !answer.id),
  );

  if (!hasAnswerWithoutId) {
    return poll;
  }

  return {
    ...poll,
    questions: poll.questions.map(
      (question): Question =>
        'answers' in question.data
          ? {
              ...question,
              data: {
                ...question.data,
                answers: question.data.answers.filter(answer => !!answer.id),
              } as any, // fixme!
            }
          : question,
    ),
  };
}

function randomizePoll(poll: Poll) {
  if (!poll.randomizePoll) return poll;
  let result = poll;

  const questions: Question[] = [];
  const groupedQuestions = groupQuestionsByType(poll);
  if (poll.randomizePoll === RANDOMIZE_POLL_GROUPS) {
    randomizeGroups({
      groupedQuestions,
      limit: poll.randomizePageLimit,
      excludedIds: poll.randomizePollExcludedIds,
    }).forEach(item => questions.push(...item.items));
  } else if (poll.randomizePoll === RANDOMIZE_POLL_SETS) {
    randomizeSets({
      groupedQuestions,
      limit: poll.randomizePageLimit,
      excludedIds: poll.randomizePollExcludedIds,
    }).forEach(item => questions.push(...item.items));
  } else if (poll.randomizePoll === RANDOMIZE_POLL_ALL) {
    randomizeAll({
      groupedQuestions,
      limit: poll.randomizePageLimit,
      excludedIds: poll.randomizePollExcludedIds,
    }).forEach(item => questions.push(...item.items));
  }

  const firstId = getFirstId(poll, questions);
  result = { ...result, firstId, questions };
  return result;
}

export function orderQuestionsUsingQuestionsIndices(poll: Poll, questionsIndices: string[]) {
  const questions: Question[] = [];
  questionsIndices.forEach(questionId => {
    const found = poll.questions.find(it => it.id === questionId);
    if (found) questions.push(found);
  });

  const firstId = getFirstId(poll, questions);
  return { ...poll, firstId, questions };
}

function randomizeAll({
  groupedQuestions,
  excludedIds,
  limit,
}: {
  groupedQuestions: IGroupedQuestion[];
  excludedIds?: string[];
  limit?: number;
}) {
  const shuffled = shuffle(groupedQuestions).filter(it => it.type !== 'auth' && !excludedIds?.includes(it.id));

  const swapMap = new Map<string, IGroupedQuestion | null>();
  groupedQuestions.forEach(item => {
    /** Шаг авторизации или исключенные из рандомизации страницы должны оставаться на своем месте */
    if (item.type === 'auth' || excludedIds?.includes(item.id)) swapMap.set(item.id, item);
    else swapMap.set(item.id, shuffled.pop() || null);
  });

  let result = groupedQuestions.map(item => swapMap.get(item.id)).filter(it => it != null);

  if (typeof limit === 'number' && limit > 0) {
    result = result.filter(item => {
      /** Если присутствует шаг авторизации, обязательно показываем его */
      if (item.type === 'auth') return true;
      /** limit не должен учитывать страницы из excludedIds */
      if (excludedIds?.includes(item.id)) return true;
      return limit!-- > 0;
    });
  }
  return result;
}

function randomizeGroups({
  groupedQuestions,
  excludedIds,
  limit,
}: {
  groupedQuestions: IGroupedQuestion[];
  excludedIds?: string[];
  limit?: number;
}) {
  const groups = groupedQuestions.filter(it => it.type === 'group' && !excludedIds?.includes(it.id));
  const shuffledGroups = shuffle(groups);

  const groupMap = new Map<string, IGroupedQuestion | null>();
  groupedQuestions.forEach(item => {
    if (item.type !== 'group') return;
    /** Исключенные из рандомизации группы должны оставаться на своем месте */
    if (excludedIds?.includes(item.id)) groupMap.set(item.id, item);
    else groupMap.set(item.id, shuffledGroups.pop() || null);
  });

  let result = groupedQuestions.map(it => (it.type === 'group' ? groupMap.get(it.id) : it)).filter(it => it != null);

  if (typeof limit === 'number' && limit > 0) {
    result = result.filter(item => {
      if (item.type !== 'group') return true;
      /** limit не должен учитывать группы из excludedIds */
      if (excludedIds?.includes(item.id)) return true;
      return limit!-- > 0;
    });
  }

  return result;
}

function randomizeSets({
  groupedQuestions,
  excludedIds,
  limit,
}: {
  groupedQuestions: IGroupedQuestion[];
  excludedIds?: string[];
  limit?: number;
}) {
  const groupedScreens = groupScreensByType(groupedQuestions);
  const sets = groupedScreens.filter(it => it.type === 'set' && !excludedIds?.includes(it.id));

  const shuffledSets = shuffle(sets);

  const setMap = new Map<string, IGroupedScreen | null>();
  groupedScreens.forEach(item => {
    if (item.type !== 'set') return;
    /** Исключенные из рандомизации группы должны оставаться на своем месте */
    if (excludedIds?.includes(item.id)) setMap.set(item.id, item);
    else setMap.set(item.id, shuffledSets.pop() || null);
  });

  let result = groupedScreens
    .map(it => (it.type === 'set' ? setMap.get(it.id) : it))
    .filter(it => it != null) as IGroupedScreen[];

  if (typeof limit === 'number' && limit > 0) {
    result = result.filter(item => {
      if (item.type !== 'set') return true;
      /** limit не должен учитывать сеты из excludedIds */
      if (excludedIds?.includes(item.id)) return true;
      return limit!-- > 0;
    });
  }

  return ungroupScreensByType(result);
}

export function getFirstId(poll: Poll, questions: Question[]) {
  let { firstId } = poll;

  const [firstQuestion] = poll.questions;
  const [firstNextQuestion] = questions;

  const firstQuestionGroupId = firstQuestion ? getQuestionGroupId(firstQuestion, poll.questionDecks) : undefined;
  const firstNextQuestionGroupId = firstNextQuestion
    ? getQuestionGroupId(firstNextQuestion, poll.questionDecks)
    : undefined;

  if (firstId && [firstQuestion?.id, firstQuestionGroupId].includes(firstId)) {
    if (firstNextQuestionGroupId) {
      firstId = firstNextQuestionGroupId;
    } else if (firstNextQuestion) {
      firstId = firstNextQuestion.id;
    }
  }
  return firstId;
}

export function resolveLinkedQuestions(poll: Poll) {
  const links: ICState['links'] = {};
  const nextQuestions = [...poll.questions];

  // добавление связи в мультиграф
  const addLink = (id1: string, id2: string) => {
    links[id1] = [...(links[id1] ?? []), id2];
    links[id2] = [...(links[id2] ?? []), id1];

    for (const { node, dependencies } of [
      { node: id1, dependencies: links[id2] ?? [] },
      { node: id2, dependencies: links[id1] ?? [] },
    ]) {
      for (const dependency of dependencies) {
        if (dependency !== node && !links[dependency]?.includes(node)) {
          links[dependency] = [...(links[dependency] ?? []), node];
          links[node] = [...(links[node] ?? []), dependency];
        }
      }
    }
  };

  const resolveLinkedQuestion = (question: Question): Question | null => {
    if (question.data.type !== 'MEDIA_FILE_LINK') {
      return null;
    }

    const linkedQuestion = poll.questions.find(
      q => 'linkedQuestionId' in question.data && q.id === question.data.linkedQuestionId,
    );

    if (linkedQuestion?.data.type === 'MEDIA_FILE_LINK') {
      return resolveLinkedQuestion(linkedQuestion);
    }

    return linkedQuestion ?? null;
  };

  for (const [index, question] of poll.questions.entries()) {
    const linkedQuestion = resolveLinkedQuestion(question);

    if (linkedQuestion) {
      addLink(linkedQuestion.id, question.id);

      nextQuestions[index] = {
        ...question,
        data: {
          ...linkedQuestion.data,
          text: question.data.text || linkedQuestion.data.text,
        },
      } as Question;
    }
  }

  return { poll: { ...poll, questions: nextQuestions }, links };
}
